import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { STATUS_META } from "../lib/workflows";
import { ArrowLeft, Loader2, Layers, Inbox, UserCircle2 } from "lucide-react";

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || { color: "text-slate-700 bg-slate-100 border-slate-300" };
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${meta.color}`}>{status}</span>;
};

export default function AdminEmployeeQueue() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/employees/${id}/queue`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load this employee's queue.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const orders = data?.orders || [];
  const inProgress = orders.filter((o) => o.status !== "Open Order").length;

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-slate-100 -m-6 p-6 text-slate-800">
      <button data-testid="emp-queue-back" onClick={() => navigate("/admin")}
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1A395E] hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to Dashboard
      </button>

      {loading ? (
        <div className="flex min-h-[40vh] items-center justify-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading queue…
        </div>
      ) : err ? (
        <div data-testid="emp-queue-error" className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-center text-rose-700">{err}</div>
      ) : (
        <>
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1A395E]/10"><UserCircle2 className="h-6 w-6 text-[#1A395E]" /></div>
            <div>
              <h1 data-testid="emp-queue-title" className="font-display text-2xl font-700 tracking-tight text-[#1A395E]">{data.employee.name}'s Queue</h1>
              <p className="text-sm text-slate-500">{data.employee.email}{data.employee.job_role ? ` · ${data.employee.job_role}` : ""} — active, non-closed orders only.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4 max-w-md">
            <div data-testid="emp-queue-kpi-active" className="rounded-lg border border-slate-200 bg-white p-3 flex items-center justify-between shadow-sm">
              <div><div className="text-[11px] uppercase font-semibold text-slate-400">Active</div><div className="text-2xl font-700 text-[#1A395E]">{orders.length}</div></div><Layers className="h-5 w-5 text-blue-500" />
            </div>
            <div data-testid="emp-queue-kpi-inprogress" className="rounded-lg border border-slate-200 bg-white p-3 flex items-center justify-between shadow-sm">
              <div><div className="text-[11px] uppercase font-semibold text-slate-400">In Progress</div><div className="text-2xl font-700 text-blue-700">{inProgress}</div></div><Inbox className="h-5 w-5 text-blue-500" />
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-600">Order</th>
                  <th className="px-3 py-2 font-600">Product</th>
                  <th className="px-3 py-2 font-600">Location</th>
                  <th className="px-3 py-2 font-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orders.length === 0 && (
                  <tr><td colSpan={4} data-testid="emp-queue-empty" className="px-3 py-8 text-center text-slate-400">No active orders in this queue.</td></tr>
                )}
                {orders.map((o) => (
                  <tr key={o.id} data-testid={`emp-queue-order-${o.id}`} onClick={() => navigate(`/orders/${o.id}`)}
                    className="cursor-pointer hover:bg-blue-50/60 transition-colors">
                    <td className="px-3 py-2 font-semibold text-[#1A395E]">{o.order_ref}</td>
                    <td className="px-3 py-2 text-slate-600">{o.product_type}</td>
                    <td className="px-3 py-2 text-slate-600">{[o.county, o.state].filter(Boolean).join(", ") || "—"}</td>
                    <td className="px-3 py-2"><StatusPill status={o.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
